import { ComponentConfigFieldType, ComponentConfigType } from "../../../../utils/component/ComponentConfig";
import { FieldPropsType } from "../../Editor";
import useReadOnly from "../useReadOnly";
import React, { Fragment, useMemo } from "react";
import { getRowIndex, rowIndexField } from "../../../List/List";
import { Button, Divider, Grid, Input, NumberInput } from "@mantine/core";
import Translation from "../../../Utils/Translation";
import { get } from "../../../../utils/object";
import ComponentConfig from "../../../Utils/ComponentConfig";
import DynamicComponent from "../../../Utils/DynamicComponent";
import Confirm, { deleteProps } from "../../../Utils/Confirm";
import { IconPlus, IconTrash } from "@tabler/icons-react";

export const view = ({ field, view, config: parentConfig, fieldSettings }: { field: string, view: Record<string, any>, config: ComponentConfigType, fieldSettings: ComponentConfigFieldType }) => {
    //const foreignKey = fieldSettings.connection.foreignKey ?? (snakeCase(parentConfig.componentName) + '_id');
    const localKey = fieldSettings.connection.localKey ?? parentConfig.idfield;
    view[localKey] = view[localKey] ?? null;
    view[field] = {
        id: null,
        project: {
            id: null,
        },
        name: null,
        material: null,
        print: null,
        punching_knife: {
            id: null,
            name: null,
            width: null,
            height: null,
            wave_direction: null,
        },
        multiplier: null,
        width: null,
        height: null,
        wave_direction: null,
    };
    return view;
};

export default (props: FieldPropsType) => {
    return <ComponentConfig component="ProjectElement" as="fieldConfig">
        <Comp {...props} fieldConfig={undefined} />
    </ComponentConfig>
}

const Comp = (props: FieldPropsType & { fieldConfig: ComponentConfigType }) => {
    const form = props.form;
    const field = props.field;
    const fieldName = field.split(".");
    const currentField = fieldName.shift();
    const fieldSettings = props.config?.fields?.[currentField];
    const value = get(form.values, field) ?? [];
    const fieldReadOnly = useReadOnly(props);
    const actions = useMemo(() => ({
        update: ({ request: { data }, props: { values } }) => {
            const newValue = [...(value ?? [])];
            const index = getRowIndex(values) ?? -1;
            if (index !== -1) {
                newValue[index] = { ...newValue[index], ...data };
            } else {
                newValue.push(data);
            }
            form.setFieldValue(field, newValue);
        },
        create: ({ request: { data }, props: { values } }) => {
            const newValue = [...(value ?? [])];
            const index = getRowIndex(values) ?? -1;
            if (index !== -1) {
                newValue[index] = { ...newValue[index], ...data };
            } else {
                newValue.push(data);
            }
            form.setFieldValue(field, newValue);
        },
        remove: ({ request: { data }, props: { values } }) => {
            const newValue = [...(value ?? [])];
            const index = getRowIndex(values) ?? -1;
            if (index !== -1) {
                newValue.splice(index, 1);
            }
            form.setFieldValue(field, newValue);
        },
        findOne: ({ props: { values: data } }) => {
            return data;
        }
    }), [form.values[props.config.idfield], JSON.stringify(value)]);
    return <>
        <Input.Wrapper
            label={<Translation base={[props.config.componentName + ".field." + currentField, props.config.componentName]} id={currentField} />}
            withAsterisk={fieldSettings.required}
            error={form.errors?.[field]}
        >
            {(value.map((element, idx) => {
                const handleChange = (idx: number, f: string, v: number | string | null) => {
                    if (String(v ?? '').trim() === '') v = null;
                    const newValue = value.map((item, i) =>
                        i === idx ? { ...item, [f]: v } : item
                    );
                    form.setFieldValue(field, newValue);
                };
                return (
                    <Fragment key={idx}>
                        <Grid key={idx}>
                            <Grid.Col xs={(fieldReadOnly || props.readOnly) ? 12 : 11}>
                                <Grid>
                                    <Grid.Col xs={3}>
                                        <DynamicComponent
                                            config={props.fieldConfig}
                                            session={props.session}
                                            path="Editor/Fields/belongsTo/String"
                                            field="name"
                                            action="create"
                                            readOnly={fieldReadOnly || props.readOnly}
                                            objectValue={true}
                                            value={undefined}
                                            form={{
                                                values: element,
                                                errors: {},
                                                setFieldValue: (f, v) => {
                                                    const newElement = { ...element };
                                                    newElement[f] = v;
                                                    actions.update({ request: { data: { ...newElement } }, props: { values: { ...newElement, [rowIndexField]: idx } } });
                                                }
                                            }}
                                        />
                                    </Grid.Col>
                                    <Grid.Col xs={5}>
                                        <DynamicComponent
                                            config={props.fieldConfig}
                                            session={props.session}
                                            path="Editor/Fields/belongsTo/Option"
                                            field="material"
                                            action="create"
                                            readOnly={fieldReadOnly || props.readOnly}
                                            objectValue={true}
                                            value={undefined}
                                            form={{
                                                values: element,
                                                errors: {},
                                                setFieldValue: (f, v) => {
                                                    const newElement = { ...element };
                                                    newElement[f] = v;
                                                    actions.update({ request: { data: { ...newElement } }, props: { values: { ...newElement, [rowIndexField]: idx } } });
                                                }
                                            }}
                                        />
                                    </Grid.Col>
                                    <Grid.Col xs={4}>
                                        <DynamicComponent
                                            config={props.fieldConfig}
                                            session={props.session}
                                            path="Editor/Fields/belongsTo/Option"
                                            field="print"
                                            action="create"
                                            readOnly={fieldReadOnly || props.readOnly}
                                            objectValue={true}
                                            value={undefined}
                                            form={{
                                                values: element,
                                                errors: {},
                                                setFieldValue: (f, v) => {
                                                    const newElement = { ...element };
                                                    newElement[f] = v;
                                                    actions.update({ request: { data: { ...newElement } }, props: { values: { ...newElement, [rowIndexField]: idx } } });
                                                }
                                            }}
                                        />
                                    </Grid.Col>
                                    <Grid.Col xs={3}>
                                        <DynamicComponent
                                            config={props.fieldConfig}
                                            session={props.session}
                                            path="Editor/Fields/belongsTo/PunchingKnife"
                                            filter={[
                                                { field: "id", operator: "not in", value: [-1, ...value.map((item) => item.punching_knife?.id).filter(Boolean)] }
                                            ]}
                                            notShowIfEmptySearch={true}
                                            field="punching_knife"
                                            action="create"
                                            readOnly={fieldReadOnly || props.readOnly}
                                            value={undefined}
                                            minimumLengthOfSearch={0}
                                            form={{
                                                values: element,
                                                errors: {},
                                                setFieldValue: (_, v) => {
                                                    const newValue = [...value];
                                                    newValue[idx] = {
                                                        ...newValue[idx],
                                                        punching_knife: v,
                                                        width: v?.width ?? null,
                                                        height: v?.height ?? null,
                                                        wave_direction: v?.wave_direction ?? null,
                                                    };
                                                    form.setFieldValue(field, newValue);
                                                }
                                            }}
                                        />
                                    </Grid.Col>
                                    <Grid.Col xs={2}>
                                        <DynamicComponent
                                            config={props.fieldConfig}
                                            session={props.session}
                                            path="Editor/Fields/belongsTo/Number"
                                            field="width"
                                            min={0}
                                            action="create"
                                            readOnly={fieldReadOnly || props.readOnly}
                                            value={element.width ?? ''}
                                            form={{
                                                values: element,
                                                errors: {},
                                                setFieldValue: (f, v) => {
                                                    const newElement = { ...element };
                                                    newElement[f] = v;
                                                    actions.update({ request: { data: { ...newElement } }, props: { values: { ...newElement, [rowIndexField]: idx } } });
                                                }
                                            }}
                                        />
                                    </Grid.Col>
                                    <Grid.Col xs={2}>
                                        <DynamicComponent
                                            config={props.fieldConfig}
                                            session={props.session}
                                            path="Editor/Fields/belongsTo/Number"
                                            field="height"
                                            min={0}
                                            action="create"
                                            readOnly={fieldReadOnly || props.readOnly}
                                            value={element.height ?? ''}
                                            form={{
                                                values: element,
                                                errors: {},
                                                setFieldValue: (f, v) => {
                                                    const newElement = { ...element };
                                                    newElement[f] = v;
                                                    actions.update({ request: { data: { ...newElement } }, props: { values: { ...newElement, [rowIndexField]: idx } } });
                                                }
                                            }}
                                        />
                                    </Grid.Col>
                                    <Grid.Col xs={2}>
                                        <DynamicComponent
                                            config={props.fieldConfig}
                                            session={props.session}
                                            path="Editor/Fields/belongsTo/Boolean"
                                            field="wave_direction"
                                            name={`wave_direction[${idx}]`}
                                            action="create"
                                            readOnly={fieldReadOnly || props.readOnly}
                                            objectValue={true}
                                            value={undefined}
                                            form={{
                                                values: element,
                                                errors: {},
                                                setFieldValue: (f, v) => {
                                                    const newElement = { ...element };
                                                    newElement[f] = v;
                                                    actions.update({ request: { data: { ...newElement } }, props: { values: { ...newElement, [rowIndexField]: idx } } });
                                                }
                                            }}
                                        />
                                    </Grid.Col>
                                    <Grid.Col xs={2}>
                                        <NumberInput
                                            label={<Translation base={["ProjectElement.field.multiplier", "ProjectElement"]} id="multiplier" fallback="Szorzó" />}
                                            value={element.multiplier ?? 1}
                                            min={1}
                                            onChange={(v) => handleChange(idx, "multiplier", v)}
                                            readOnly={fieldReadOnly || props.readOnly}
                                            radius="md"
                                        />
                                    </Grid.Col>
                                </Grid>
                            </Grid.Col>
                            {(fieldReadOnly || props.readOnly) ? null : <Grid.Col xs={1}>
                                <Input.Wrapper
                                    label={<>&nbsp;</>}
                                >
                                    <Confirm
                                        {...deleteProps}
                                        onAccept={() => {
                                            actions.remove({ request: { data: { ...element } }, props: { values: { ...element, [rowIndexField]: idx } } });
                                        }}
                                    >
                                        <Button
                                            variant="subtle"
                                            fullWidth
                                            disabled={fieldReadOnly || props.readOnly}
                                        >
                                            <IconTrash />
                                        </Button>
                                    </Confirm>
                                </Input.Wrapper>
                            </Grid.Col>}
                        </Grid>
                        <Divider my="sm" />
                    </Fragment>
                );
            }))}
            {(fieldReadOnly || props.readOnly) ? null : <Grid>
                <Grid.Col xs={12}>
                    <Button
                        variant="subtle"
                        fullWidth
                        disabled={fieldReadOnly || props.readOnly}
                        onClick={() => {
                            actions.create({ request: { data: {} }, props: { values: {} } });
                        }}
                    >
                        <IconPlus />
                    </Button>
                </Grid.Col>
            </Grid>}
        </Input.Wrapper>
    </>;
};